import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

interface ChatInstructionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (instruction: { id: string; name: string; content: string }) => void;
}

const STUDY_MODE_TEMPLATE = {
  id: "study-mode-template",
  name: "Study Mode",
  description: "Socratic-style, step-by-step guide",
  content: "Guide me through this topic using the Socratic method. Ask me questions to help me understand concepts deeply, one step at a time. Don't give me answers directly - help me discover them.",
};

export const ChatInstructionsDialog: React.FC<ChatInstructionsDialogProps> = ({
  open,
  onOpenChange,
  onSelect,
}) => {
  const [selectedOption, setSelectedOption] = useState<"study-mode" | "custom">("study-mode");
  const [promptName, setPromptName] = useState("");
  const [promptContent, setPromptContent] = useState("");

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedOption === "study-mode") {
      onSelect({
        id: STUDY_MODE_TEMPLATE.id,
        name: STUDY_MODE_TEMPLATE.name,
        content: STUDY_MODE_TEMPLATE.content,
      });
    } else {
      if (!promptName.trim() || !promptContent.trim()) return;
      onSelect({
        id: `custom-${Date.now()}`,
        name: promptName.trim(),
        content: promptContent.trim(),
      });
    }
    setSelectedOption("study-mode");
    setPromptName("");
    setPromptContent("");
    onOpenChange(false);
  };

  const handleCancel = () => {
    setSelectedOption("study-mode");
    setPromptName("");
    setPromptContent("");
    onOpenChange(false);
  };

  const isValid = selectedOption === "study-mode" || (promptName.trim() && promptContent.trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-xl p-0 gap-0">
        <DialogHeader className="py-4 px-6 pb-0 mb-4">
          <DialogTitle className="text-lg font-normal">Chat instructions</DialogTitle>
          <p className="text-sm text-muted-foreground">Choose or create a custom chat prompt</p>
        </DialogHeader>

        <form onSubmit={handleCreate}>
          <div className="flex flex-1 flex-col gap-3 px-6 py-2 pt-0">
            <div className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-3">
                  {/* Templates label */}
                  <div className="space-y-2">
                    <span className="text-sm font-medium">Templates</span>

                    {/* Study Mode card */}
                    <div
                      onClick={() => setSelectedOption("study-mode")}
                      className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-colors ${
                        selectedOption === "study-mode"
                          ? "border-primary bg-primary/5"
                          : ""
                      }`}
                    >
                      <div className={`flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                        selectedOption === "study-mode" ? "border-primary" : "border-muted-foreground"
                      }`}>
                        {selectedOption === "study-mode" && (
                          <div className="h-2 w-2 rounded-full bg-primary" />
                        )}
                      </div>
                      <div className="flex-1">
                        <span className="text-sm font-medium">{STUDY_MODE_TEMPLATE.name}</span>
                        <div className="mt-1 text-xs text-muted-foreground">
                          <p>{STUDY_MODE_TEMPLATE.description}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Create new prompt card */}
                  <div
                    onClick={() => setSelectedOption("custom")}
                    className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-colors ${
                      selectedOption === "custom"
                        ? "border-primary bg-primary/5"
                        : ""
                    }`}
                  >
                    <div className={`flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                      selectedOption === "custom" ? "border-primary" : "border-muted-foreground"
                    }`}>
                      {selectedOption === "custom" && (
                        <div className="h-2 w-2 rounded-full bg-primary" />
                      )}
                    </div>
                    <div className="flex-1">
                      <span className="text-sm font-medium">Create new prompt</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Custom Prompt Fields */}
              {selectedOption === "custom" && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="newPromptName">Prompt Name</Label>
                    <Input
                      id="newPromptName"
                      placeholder="Enter prompt name"
                      value={promptName}
                      onChange={(e) => setPromptName(e.target.value)}
                      required
                      className="mt-1 rounded-lg"
                    />
                  </div>
                  <div>
                    <Label htmlFor="newPromptContent">Prompt Content</Label>
                    <Textarea
                      id="newPromptContent"
                      placeholder="Enter your custom chat prompt"
                      value={promptContent}
                      onChange={(e) => setPromptContent(e.target.value)}
                      rows={4}
                      required
                      className="mt-1 resize-none rounded-xl p-3 max-h-[80px] min-h-[45px]"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <footer className="flex flex-row gap-2 px-6 py-4 justify-end">
            <Button type="button" variant="secondary" className="h-10 px-4 py-2 rounded-lg" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit" className="h-10 px-4 py-2 rounded-lg" disabled={!isValid}>
              Create
            </Button>
          </footer>
        </form>
      </DialogContent>
    </Dialog>
  );
};
